
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2, AlertCircle, Book, Download, Share2, Copy, CheckCircle, Calendar, Play, ExternalLink, Facebook, Twitter, MessageCircle } from 'lucide-react';
import { format } from 'date-fns';
import ReactPlayer from 'react-player/youtube';

interface SeriesData {
  id: string;
  title: string;
  description: string | null;
  user_id: string;
}

interface SharedSeriesData {
  id: string;
  series_id: string;
  user_id: string;
  created_at: string;
  summary: string | null;
}

interface SermonData {
  id: string;
  title: string;
  scripture: string;
  content: string;
  date: string | null;
  youtube_url: string | null;
}

export function SharedSeries() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [series, setSeries] = useState<SeriesData | null>(null);
  const [sharedData, setSharedData] = useState<SharedSeriesData | null>(null);
  const [sermons, setSermons] = useState<SermonData[]>([]);
  const [linkCopied, setLinkCopied] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedSeries = async () => {
      try {
        setLoading(true);

        console.log('Fetching shared series with ID:', id);
        
        // Get the shared series data
        const { data: sharedSeriesData, error: sharedError } = await supabase
          .from('shared_series')
          .select('*')
          .eq('id', id)
          .single();

        if (sharedError) {
          console.error('Error fetching shared series:', sharedError);
          throw sharedError;
        }
        if (!sharedSeriesData) throw new Error('Shared series not found');

        console.log('Shared series data:', sharedSeriesData);
        setSharedData(sharedSeriesData);

        // Get the actual series data
        console.log('Fetching series with ID:', sharedSeriesData.series_id);
        const { data: seriesData, error: seriesError } = await supabase
          .from('series')
          .select('*')
          .eq('id', sharedSeriesData.series_id)
          .single();

        if (seriesError) {
          console.error('Error fetching series:', seriesError);
          throw seriesError;
        }
        if (!seriesData) throw new Error('Series not found');

        console.log('Series data:', seriesData);
        setSeries(seriesData);

        // Get all sermons in the series
        const { data: sermonsData, error: sermonsError } = await supabase
          .from('sermons')
          .select('id, title, scripture, content, date, youtube_url')
          .eq('series_id', sharedSeriesData.series_id)
          .eq('archived', false)
          .order('date', { ascending: true });

        if (sermonsError) throw sermonsError;

        setSermons(sermonsData || []);

        // Set the first YouTube video as default if available
        const firstVideoSermon = sermonsData?.find(sermon => sermon.youtube_url);
        if (firstVideoSermon?.youtube_url) {
          setSelectedVideoUrl(firstVideoSermon.youtube_url);
        }

      } catch (error) {
        console.error('Error fetching shared series:', error);
        setError(error instanceof Error ? error.message : 'Failed to load shared series');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSharedSeries();
    }
  }, [id]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (e) {
      return '';
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleSocialShare = (platform: string) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(`${series?.title} - Sermon Series`);
    const description = encodeURIComponent(sharedData?.summary || `Check out this sermon series: ${series?.title}`);

    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${title}%20${url}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const handleDownloadStudyGuide = async (sermonId: string, sermonTitle: string) => {
    try {
      // Check if there's a shared sermon with summary and questions
      const { data: sharedSermon } = await supabase
        .from('shared_sermons')
        .select('summary, questions')
        .eq('sermon_id', sermonId)
        .maybeSingle();

      if (sharedSermon?.summary && sharedSermon?.questions) {
        // Get the sermon details
        const sermon = sermons.find(s => s.id === sermonId);
        if (!sermon) {
          alert('Sermon details not found.');
          return;
        }

        // Generate the study guide PDF
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        
        let yPos = 30;

        // Add title
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.text('Small Group Discussion Guide', pageWidth / 2, yPos, { align: 'center' });
        yPos += 20;

        // Add sermon title
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text(sermon.title, pageWidth / 2, yPos, { align: 'center' });
        yPos += 15;

        // Add scripture
        if (sermon.scripture) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(12);
          doc.text(`Scripture: ${sermon.scripture}`, pageWidth / 2, yPos, { align: 'center' });
          yPos += 20;
        }

        // Add summary section
        if (sharedSermon.summary) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text('Sermon Summary', 20, yPos);
          yPos += 15;

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(11);
          const summaryLines = doc.splitTextToSize(sharedSermon.summary, pageWidth - 40);
          doc.text(summaryLines, 20, yPos);
          yPos += (summaryLines.length * 6) + 20;
        }

        // Add questions section
        if (sharedSermon.questions && sharedSermon.questions.length > 0) {
          // Check if we need a new page
          if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 30;
          }

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text('Discussion Questions', 20, yPos);
          yPos += 15;

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(11);

          sharedSermon.questions.forEach((question: string, index: number) => {
            if (yPos > pageHeight - 60) {
              doc.addPage();
              yPos = 30;
            }

            const questionText = `${index + 1}. ${question}`;
            const questionLines = doc.splitTextToSize(questionText, pageWidth - 40);
            doc.text(questionLines, 20, yPos);
            yPos += (questionLines.length * 6) + 15;
          });
        }

        // Add notes section
        if (yPos > pageHeight - 100) {
          doc.addPage();
          yPos = 30;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('Notes', 20, yPos);
        yPos += 20;

        // Add lines for notes
        for (let i = 0; i < 8; i++) {
          if (yPos > pageHeight - 30) break;
          doc.setDrawColor(200, 200, 200);
          doc.line(20, yPos, pageWidth - 20, yPos);
          yPos += 15;
        }

        // Download the PDF
        const fileName = `${sermonTitle.toLowerCase().replace(/\s+/g, '-')}-study-guide.pdf`;
        doc.save(fileName);
      } else {
        // Redirect to individual sermon page for full functionality
        const sharedSermonRecord = await supabase
          .from('shared_sermons')
          .select('id')
          .eq('sermon_id', sermonId)
          .maybeSingle();

        if (sharedSermonRecord?.data?.id) {
          window.open(`/shared/sermon/${sharedSermonRecord.data.id}`, '_blank');
        } else {
          alert('Study guide not available for this sermon. Please visit the individual sermon page to generate one.');
        }
      }
    } catch (error) {
      console.error('Error downloading study guide:', error);
      alert('Failed to download study guide. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand/10 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-electric" />
      </div>
    );
  }

  if (error || !series || !sharedData) {
    return (
      <div className="min-h-screen bg-sand/10 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-4 md:p-8">
            <div className="flex items-center text-crimson mb-4">
              <AlertCircle className="h-6 w-6 mr-2" />
              <h2 className="text-xl font-semibold">Error Loading Series</h2>
            </div>
            <p className="text-slate mb-6">{error || 'The shared series could not be found or has expired.'}</p>
            <Link to="/" className="text-electric hover:text-electric/80">
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const videosAvailable = sermons.filter(sermon => sermon.youtube_url).length;

  const metaTitle = `${series.title} — Sermon Series | SermonBuild`;
  const metaDescription = (() => {
    const base = sharedData.summary || series.description || '';
    return base
      ? base.substring(0, 155)
      : `${series.title} — a sermon series with ${sermons.length} message${sermons.length !== 1 ? 's' : ''}. Read on SermonBuild.`;
  })();

  return (
    <div className="min-h-screen bg-sand/5 pb-16">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-midnight mb-2">{series.title}</h1>
          {series.description && (
            <p className="text-lg text-slate mb-4">{series.description}</p>
          )}
          <div className="text-sm text-slate mb-4">
            {sermons.length} sermon{sermons.length !== 1 ? 's' : ''} in this series
            {videosAvailable > 0 && ` • ${videosAvailable} video${videosAvailable !== 1 ? 's' : ''} available`}
          </div>

          {/* Share buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleCopyLink}
              className="flex items-center px-3 py-1.5 bg-electric text-white rounded-lg hover:bg-electric/90 text-sm"
            >
              {linkCopied ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy Link
                </>
              )}
            </button>

            <button
              onClick={() => handleSocialShare('facebook')}
              className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Facebook className="h-4 w-4 mr-1" />
              Facebook
            </button>

            <button
              onClick={() => handleSocialShare('twitter')}
              className="flex items-center px-3 py-1.5 bg-sky-500 text-white rounded-lg hover:bg-sky-600 text-sm"
            >
              <Twitter className="h-4 w-4 mr-1" />
              Twitter
            </button>

            <button
              onClick={() => handleSocialShare('whatsapp')}
              className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              <MessageCircle className="h-4 w-4 mr-1" />
              WhatsApp
            </button>
          </div>
        </div>

        {/* Video Player */}
        {selectedVideoUrl && (
          <div className="mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-midnight mb-4">Series Videos</h2>
            <div className="relative w-full bg-black rounded-lg overflow-hidden shadow-md" style={{ paddingBottom: '56.25%' }}>
              <ReactPlayer
                url={selectedVideoUrl}
                width="100%"
                height="100%"
                controls={true}
                playing={false}
                light={false}
                pip={true}
                stopOnUnmount={false}
                style={{ position: 'absolute', top: 0, left: 0 }}
                config={{
                  youtube: {
                    playerVars: {
                      autoplay: 0,
                      controls: 1,
                      disablekb: 0,
                      enablejsapi: 1,
                      fs: 1,
                      modestbranding: 1,
                      rel: 0,
                      showinfo: 1
                    }
                  }
                }}
              />
            </div>

            {/* Video selector */}
            {videosAvailable > 1 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {sermons.filter(sermon => sermon.youtube_url).map((sermon) => (
                  <button
                    key={sermon.id}
                    onClick={() => setSelectedVideoUrl(sermon.youtube_url)}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${
                      selectedVideoUrl === sermon.youtube_url
                        ? 'bg-electric text-white border-electric'
                        : 'bg-white text-midnight border-sand/20 hover:bg-sand/10'
                    }`}
                  >
                    <Play className="h-3 w-3 mr-1 inline" />
                    {sermon.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Series Summary */}
            {sharedData.summary && (
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-midnight mb-4">Series Overview</h2>
                <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
                  <p className="text-slate leading-relaxed">{sharedData.summary}</p>
                </div>
              </div>
            )}

            {/* Sermons List */}
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-midnight mb-4">Sermons in This Series</h2>
              <div className="space-y-4">
                {sermons.map((sermon, index) => (
                  <div key={sermon.id} className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-electric text-white text-sm font-bold rounded-full">
                            {index + 1}
                          </span>
                          <h3 className="text-lg font-semibold text-midnight">{sermon.title}</h3>
                        </div>
                        <p className="text-slate mb-2">{sermon.scripture}</p>
                        {sermon.date && (
                          <div className="flex items-center text-sm text-slate">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(sermon.date)}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {sermon.youtube_url && (
                          <button
                            onClick={() => setSelectedVideoUrl(sermon.youtube_url)}
                            className="p-2 text-crimson hover:bg-crimson/10 rounded-lg"
                            title="Watch Video"
                          >
                            <Play className="h-4 w-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleDownloadStudyGuide(sermon.id, sermon.title)}
                          className="p-2 text-electric hover:bg-electric/10 rounded-lg"
                          title="Download Study Guide"
                        >
                          <Download className="h-4 w-4" />
                        </button>

                        <Link
                          to={`/shared/sermon/${sermon.id}`}
                          className="p-2 text-emerald hover:bg-emerald/10 rounded-lg"
                          title="View Full Sermon"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Series Stats */}
            <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
              <h3 className="font-semibold text-midnight mb-4">Series Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate">Total Sermons:</span>
                  <span className="font-medium text-midnight">{sermons.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate">Videos Available:</span>
                  <span className="font-medium text-midnight">{videosAvailable}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate">Date Range:</span>
                  <span className="font-medium text-midnight">
                    {sermons.length > 0 && sermons[0].date && sermons[sermons.length - 1].date
                      ? `${formatDate(sermons[0].date)} - ${formatDate(sermons[sermons.length - 1].date)}`
                      : 'Various dates'
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
              <h3 className="font-semibold text-midnight mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center justify-center px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Series
                </button>
                
                <button
                  onClick={async () => {
                    try {
                      // Generate a comprehensive study guide for the entire series
                      const { jsPDF } = await import('jspdf');
                      const doc = new jsPDF();
                      const pageWidth = doc.internal.pageSize.width;
                      const pageHeight = doc.internal.pageSize.height;
                      
                      let yPos = 30;

                      // Add series title
                      doc.setFont('helvetica', 'bold');
                      doc.setFontSize(20);
                      doc.text(`${series.title} - Complete Study Guide`, pageWidth / 2, yPos, { align: 'center' });
                      yPos += 20;

                      // Add series description
                      if (series.description) {
                        doc.setFont('helvetica', 'normal');
                        doc.setFontSize(12);
                        const descLines = doc.splitTextToSize(series.description, pageWidth - 40);
                        doc.text(descLines, pageWidth / 2, yPos, { align: 'center' });
                        yPos += (descLines.length * 6) + 20;
                      }

                      // Add series overview
                      if (sharedData?.summary) {
                        doc.setFont('helvetica', 'bold');
                        doc.setFontSize(14);
                        doc.text('Series Overview', 20, yPos);
                        yPos += 15;

                        doc.setFont('helvetica', 'normal');
                        doc.setFontSize(11);
                        const overviewLines = doc.splitTextToSize(sharedData.summary, pageWidth - 40);
                        doc.text(overviewLines, 20, yPos);
                        yPos += (overviewLines.length * 6) + 20;
                      }

                      // Add sermon list
                      doc.setFont('helvetica', 'bold');
                      doc.setFontSize(14);
                      doc.text('Sermons in This Series', 20, yPos);
                      yPos += 15;

                      doc.setFont('helvetica', 'normal');
                      doc.setFontSize(11);

                      sermons.forEach((sermon, index) => {
                        if (yPos > pageHeight - 40) {
                          doc.addPage();
                          yPos = 30;
                        }

                        const sermonLine = `${index + 1}. ${sermon.title} - ${sermon.scripture}`;
                        if (sermon.date) {
                          const dateStr = formatDate(sermon.date);
                          doc.text(`${sermonLine} (${dateStr})`, 20, yPos);
                        } else {
                          doc.text(sermonLine, 20, yPos);
                        }
                        yPos += 12;
                      });

                      // Add notes section
                      yPos += 20;
                      if (yPos > pageHeight - 100) {
                        doc.addPage();
                        yPos = 30;
                      }

                      doc.setFont('helvetica', 'bold');
                      doc.setFontSize(14);
                      doc.text('Notes', 20, yPos);
                      yPos += 20;

                      // Add lines for notes
                      for (let i = 0; i < 15; i++) {
                        if (yPos > pageHeight - 30) {
                          doc.addPage();
                          yPos = 30;
                        }
                        doc.setDrawColor(200, 200, 200);
                        doc.line(20, yPos, pageWidth - 20, yPos);
                        yPos += 15;
                      }

                      // Download the PDF
                      const fileName = `${series.title.toLowerCase().replace(/\s+/g, '-')}-complete-study-guide.pdf`;
                      doc.save(fileName);
                    } catch (error) {
                      console.error('Error generating series study guide:', error);
                      alert('Failed to generate series study guide. Please try again.');
                    }
                  }}
                  className="w-full flex items-center justify-center px-4 py-2 bg-emerald text-white rounded-lg hover:bg-emerald/90"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download All Guides
                </button>
              </div>
            </div>

            {/* Scripture References */}
            {sermons.some(sermon => sermon.scripture) && (
              <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
                <h3 className="font-semibold text-midnight mb-4">Scripture References</h3>
                <div className="space-y-2">
                  {sermons.filter(sermon => sermon.scripture).map((sermon, index) => (
                    <div key={sermon.id} className="text-sm">
                      <span className="text-slate">{index + 1}. </span>
                      <span className="text-midnight">{sermon.scripture}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-white border-t border-sand/20 mt-8 py-6">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-slate mb-4 md:mb-0">
              Shared via SermonBuild
            </div>
            <Link
              to="/"
              className="text-electric hover:text-electric/80 text-sm"
            >
              Create your own sermon series with SermonBuild
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
